import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertProps,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Modal,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import {
  BASELINE_2025_Q1_DUE_DATE,
  buildSubmitBaselineRevokeRequests,
  buildSubmitBaselineWithQuestionnaireRequests,
  fetchBaselineNeededConsumerDataPermissions,
} from 'src/components/permissions/myBaselining/baseliningUtils';
import { MARKETPLACE_DATA_ACCESS_STANDARD_URL, MARKETPLACE_FAQ_URL, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import { submitBaseline } from 'src/api/notifications';
import { SubmitBaselineRequest } from 'aws-sdk/clients/awsdlomni';
import { Link } from 'src/components/common/Link';
import { BaselineQuestionnaireForm } from 'src/components/permissions/myBaselining/baselineQuestionnaireForm';

export interface BaselineDataAccessPermissionProps {
  setContentType: any;
  match: any;
  username: string;
}

export const BaselineHammerstoneGroups = (props: BaselineDataAccessPermissionProps) => {
  const [keepLoading, setKeepLoading] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertType, setAlertType] = useState('success' as AlertProps.Type);
  const [alertMessage, setAlertMessage] = useState('There are no pending access to review');
  const [marketplaceDataAccessStandardsLinkClicked, setMarketplaceDataAccessStandardsLinkClicked] = useState(false);
  const [acknowledgedMarketplaceDataAccessStandards, setAcknowledgedMarketplaceDataAccessStandards] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingBaseline, setLoadingBaseline] = useState(true);
  const [mpDataPermissions, setMPDataPermissions] = useState([]);
  const [termsAndConditionsErrorMsg, setTermsAndConditionsErrorMsg] = useState('');
  const [questionnaireList, setQuestionnaireList] = useState([]);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [props.match.params.ownerId]);

  const handleRefresh = async () => {
    const ownerId = props.match.params.ownerId;
    let dps = await fetchBaselineNeededConsumerDataPermissions([ownerId]);
    dps = dps.filter(
      (dp) =>
        dp.baselineInfo?.baselineStatus === 'SCHEDULED' &&
        dp.baselineInfo?.useCase != null &&
        dp.baselineInfo?.baselineDueDate != null &&
        BASELINE_2025_Q1_DUE_DATE == dp.baselineInfo?.baselineDueDate,
    );
    setMPDataPermissions(dps);
    if (dps.length > 0) {
      setModalVisible(true);
    } else {
      dismissModal('There are no pending access to review.');
    }
    setLoadingBaseline(false);
  };

  const isValidForm = () => {
    const validations = [
      {
        condition: !acknowledgedMarketplaceDataAccessStandards,
        setError: setTermsAndConditionsErrorMsg,
        errorMessage:
          'You must accept all terms and conditions to complete the access review. Make sure to open and review all links in the form.',
      },
    ];

    let isValid = true;

    validations.forEach(({ condition, setError, errorMessage }) => {
      if (condition) {
        setError(errorMessage);
        isValid = false;
      } else {
        setError('');
      }
    });

    return isValid;
  };

  const handleSubmitBaseline = async (request: SubmitBaselineRequest, successMessage: string): Promise<void> => {
    setKeepLoading(true);
    try {
      await submitBaseline(request);
      setAlertType('success');
      setAlertMessage(successMessage);
    } catch (error) {
      setAlertType('error');
      setAlertMessage(`Failed to submit the form. Please try again later, or contact Omni oncall. ${error}`);
      console.error(error);
    } finally {
      setAlertVisible(true);
      setKeepLoading(false);
      setModalVisible(false);
    }
  };

  const handleRelinquish = async () => {
    let requests = buildSubmitBaselineRevokeRequests(mpDataPermissions);
    await Promise.all(
      requests.map((request) =>
        handleSubmitBaseline(
          request,
          'Relinquish request for access to Marketplace specific records has been submitted successfully',
        ),
      ),
    );
  };

  const handleKeep = async () => {
    if (!isValidForm()) {
      return;
    }

    let requests = buildSubmitBaselineWithQuestionnaireRequests(questionnaireList);
    await Promise.all(
      requests.map((request) =>
        handleSubmitBaseline(
          request,
          'Successfully submitted the form. You can now access Marketplace specific records in your queries within AWSDW owned Redshift cluster(s)',
        ),
      ),
    );
  };

  const dismissModal = (message = 'No action has been performed, refresh the page to continue with the review.') => {
    setModalVisible(false);
    setAlertVisible(true);
    setAlertMessage(message);
    setAlertType('info');
  };

  return (
    <>
      {loadingBaseline && <Spinner size='large'></Spinner>}
      {alertVisible && (
        <Alert type={alertType} dismissible={false}>
          {alertMessage}
        </Alert>
      )}
      <Modal
        visible={modalVisible}
        onDismiss={() => dismissModal()}
        size='max'
        header={
          <Header
            variant='h2'
            description='You have access to AWS Marketplace (MP) data in AWSDW redshift clusters. Users with access to
            MP data are required to provide an up to date business justification.'
            info={
              <Link external={true} href={MARKETPLACE_FAQ_URL}>
                info
              </Link>
            }
          >
            AWS Data Platform Marketplace data access
          </Header>
        }
        footer={
          <Box float='right'>
            <SpaceBetween size='m' direction='horizontal'>
              <Button variant='normal' loading={keepLoading} onClick={handleRelinquish}>
                Relinquish access to Marketplace records
              </Button>
              <Button variant='primary' loading={keepLoading} onClick={handleKeep}>
                Keep access to Marketplace records
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size='m' direction='vertical'>
          <BaselineQuestionnaireForm
            {...props}
            dataPermissionList={mpDataPermissions}
            questionnaireList={questionnaireList}
            setQuestionnaireList={setQuestionnaireList}
          />
          <Container header={<Header variant={'h2'}>Terms and conditions</Header>}>
            <ColumnLayout>
              <FormField
                label={'AWS Marketplace data access standard'}
                description={
                  'You have access to AWS Marketplace data. Have you read the AWS Marketplace data access standard?'
                }
                errorText={termsAndConditionsErrorMsg}
              >
                <Checkbox
                  onChange={({ detail }) => setAcknowledgedMarketplaceDataAccessStandards(detail.checked)}
                  checked={acknowledgedMarketplaceDataAccessStandards}
                  disabled={!(marketplaceDataAccessStandardsLinkClicked || acknowledgedMarketplaceDataAccessStandards)}
                >
                  I have read the&nbsp;
                  <Link
                    external={true}
                    href={MARKETPLACE_DATA_ACCESS_STANDARD_URL}
                    onClick={() => setMarketplaceDataAccessStandardsLinkClicked(true)}
                  >
                    AWS Marketplace data access standard
                  </Link>{' '}
                  and I understand that I will be responsible for compliance with the standard.
                </Checkbox>
              </FormField>
            </ColumnLayout>
          </Container>
        </SpaceBetween>
      </Modal>
    </>
  );
};
